import { GetServerSideProps, GetServerSidePropsContext } from "next/types";
import { useEffect, useState } from "react";

import {
  InsuranceGiLandingPageMainWrapper,
  InsuranceLanding,
  InsuranceLandingPageMainWrapper,
} from "@insurance/landing-ui/pages";
import { getAnalytics, setLandingPageAnalytics } from "@tm/insurance/analytics";
import { navigationActionsSeo } from "@tm/insurance/navigation";
import { getCookieByName, getDeviceIdForServer } from "@tm/insurance/utils";
import { PARTNER, PartnerType } from "@tm/isomorphic-utils";
import { getCommonsHeaderData } from "@tm/server-api-utils";
import { getPartnerOnServerSide } from "@tm/server-utils";
import {
  DeviceContextV2Type,
  withDeviceAndThemeProvider,
} from "@tm/ui-widgets";
import { PartnerContextProvider } from "@tm/ui-widgets/context";
import { withReferrerProvider } from "@tm/ui-widgets/hoc";
import {
  withError,
  withGIInsuranceHeader,
  withMMTInsuranceHeader,
} from "@tm/ui/hoc";
import "pwa-mmt-ui-header/ssr/header.css";

import { initGlobalErrorLogging } from "@tm/utility/analytics";
import { getSeoLandingPageDetails } from "../../src/api";
import { withSeoHeadTags } from "../../src/hoc";
import { seoGSSP } from "../../src/utils/seoGSSP";

const analytics = getAnalytics();

export interface ILandingPageProps {
  data?: any;
  statusCode?: string;
  error?: any;
  device: DeviceContextV2Type;
  partner: PartnerType;
  referrer: string;
  experiment?: string;
  isMyBiz?: boolean;
  commonsHeaderData?: any;
}

export function LandingPage({
  data,
  device = "mobile",
  error,
  partner,
  referrer,
  commonsHeaderData,
}: ILandingPageProps) {
  const headTags = data?.headTags || {};
  const [activeModal, setActiveModal] = useState("");

  useEffect(() => {
    const cleanup = initGlobalErrorLogging("seo_landing");
    return cleanup;
  }, []);

  setLandingPageAnalytics(data?.analytics?.pageLoad);
  const longImageUrl = data?.offers?.items?.[0]?.image_url;

  const goToMyOrders = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "my_orders_clicked",
      }
      //'click'
    );

    const authCookie =
      getCookieByName("mmt-auth") || getCookieByName("OAUTH-GOIBIBO");

    if (authCookie) {
      navigationActionsSeo.navigateToOrderListing();
    } else {
      setActiveModal?.("LOGIN");
    }
  };

  // TODO: move header configurations to context
  const component =
    partner === PARTNER.GI
      ? withGIInsuranceHeader(
          <PartnerContextProvider partnerType={PARTNER.GI}>
            <InsuranceLanding setActiveModal={setActiveModal} data={data} />
          </PartnerContextProvider>,
          {
            Wrapper: InsuranceGiLandingPageMainWrapper,
            forceLogin: activeModal ? true : false,
          },
          "Secure International Travel",
          true,
          goToMyOrders,
          false,
          false
        )
      : withMMTInsuranceHeader(
          <PartnerContextProvider partnerType={PARTNER.MMT}>
            <InsuranceLanding setActiveModal={setActiveModal} data={data} />
          </PartnerContextProvider>,
          {
            type: "dualHeader",
            Wrapper: InsuranceLandingPageMainWrapper,
            activeModal: activeModal ? "LOGIN" : undefined,
            onModalChange: (modal) => setActiveModal(modal),
            globalConfig: commonsHeaderData?.headerConfig?.value,
            headerCommonData: {
              loyaltyHeaderData: commonsHeaderData?.loyaltyHeaderData?.value,
              walletHeaderData: commonsHeaderData?.walletHeaderData?.value,
              mobileCore: commonsHeaderData?.mobileCore?.value,
              userInfoData: commonsHeaderData?.userInfoData?.value,
            },
          },
          "Secure International Travel",
          true,
          goToMyOrders,
          false,
          false,
          false,
          false
        );

  return withError(
    withReferrerProvider(
      withDeviceAndThemeProvider(
        withSeoHeadTags(component, headTags, partner, longImageUrl),
        device,
        {
          pageTheme: partner,
        }
      ),
      referrer
    ),
    error,
    "LANDING"
  );
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { req, query, res } = ctx;
  const { cmp = "" } = query || {};

  const partner = getPartnerOnServerSide(req?.headers);
  const dvid = getDeviceIdForServer(req, res, partner);

  let commonsHeaderData = null;
  if (partner !== PARTNER.GI) {
    const profile =
      partner === PARTNER.MYBIZ
        ? "BUSINESS"
        : partner === PARTNER.MYPARTNER
        ? "CTA"
        : "PERSONAL";
    commonsHeaderData = await getCommonsHeaderData(
      dvid,
      profile,
      req?.cookies?.["mmt-auth"]
    );
  }

  // Convert PartnerType to the restrictive type expected by getSeoLandingPageDetails
  // SEO pages only support "gi" and "mmt", defaulting mypartner to "mmt"
  const seoPartner: "gi" | "mmt" = partner === PARTNER.GI ? "gi" : "mmt";

  return seoGSSP(
    ctx,
    () => getSeoLandingPageDetails(seoPartner),
    partner,
    cmp,
    commonsHeaderData
  );
};

export default LandingPage;
